import React from 'react';

import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';

import { EmployeeSurveyResponse } from './survey-response-selector';
import { retryMessage } from '../../messages/actions/retry-message';
import { scheduleSurveyResponseMessage } from '../actions/survey/schedule-survey-response-message';
import { updateSurveyResponse } from '../actions/survey/update-survey-response';

import { UiButton } from '../../ui/ui-button';
import { employeeMessagesRoute } from '../../web-routes';

import { formatDateTime } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';

type InputProps = {
  orgId: number;
  employeeResponse: EmployeeSurveyResponse;
  displayIrrelevant: boolean | false;
  handleEmployeeNameClick: any;
  handleEditClick: any;
  cleanOptionalString: any;
  isAnonymousSurvey: boolean;
};
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    updateSurveyResponse: (
      surveyResponseId: number,
      employeeId: string | number,
      isRelevant: boolean,
    ) => dispatch(updateSurveyResponse(surveyResponseId, { isRelevant }, employeeId)),
    scheduleSurveyResponseMessage: (surveyResponseId: number) =>
      dispatch(scheduleSurveyResponseMessage(surveyResponseId)),
    retryMessage: (surveyResponseId: number, messageId: number) =>
      dispatch(retryMessage(messageId, { surveyResponseId })),
  },
});

const openImageInNewTab = (imageUrl: string) => () => {
  window.open(imageUrl);
};

export const EmployeeSurveyDetailRow = connect(
  null,
  mapDispatchToProps,
)((props: Props) => {
  const {
    actions,
    orgId,
    employeeResponse,
    displayIrrelevant,
    handleEmployeeNameClick,
    handleEditClick,
    cleanOptionalString,
    isAnonymousSurvey,
  } = props;

  const handleToggleRelevance =
    (surveyResponseId: number, employeeId: string | number, isRelevant: boolean) => () =>
      actions.updateSurveyResponse(surveyResponseId, employeeId, !isRelevant);
  const handleRetryMessage =
    (surveyResponseId: number, retryMessageId: number | undefined) => () => {
      if (retryMessageId) {
        actions.retryMessage(surveyResponseId, retryMessageId);
      } else {
        actions.scheduleSurveyResponseMessage(surveyResponseId);
      }
    };

  const filteredResponses = employeeResponse.responses.filter((response) =>
    !displayIrrelevant ? response.isRelevant : true,
  );

  const employeeResponseErrorMessage = () => {
    if (employeeResponse.canRetry) {
      return employeeResponse.errorMessage;
    }
    if (
      isAnonymousSurvey &&
      employeeResponse.errorIsFixedByPhoneNumberUpdate &&
      employeeResponse.errorMessage
    ) {
      // cut off the end that says "for <employee name>. Click to update...."
      return employeeResponse.errorMessage.split('for')[0];
    }

    return employeeResponse.errorMessage;
  };

  return !employeeResponse.hasAnswer ? (
    <TableRow key={employeeResponse.employeeId}>
      <TableCell style={{ width: '1em' }}>
        {!employeeResponse.hasError ? (
          <MaterialDesignIcon iconName={ICON_NAMES.REMOVE} color="gray" />
        ) : (
          <Tooltip title={employeeResponseErrorMessage() || ''}>
            {employeeResponse.canRetry ? (
              <MaterialDesignIcon
                iconName={ICON_NAMES.REPLAY}
                color="secondary"
                onClick={handleRetryMessage(
                  employeeResponse.firstSurveyResponseId,
                  employeeResponse.retryMessageId,
                )}
              />
            ) : employeeResponse.errorIsFixedByPhoneNumberUpdate ? (
              <MaterialDesignIcon
                iconName={ICON_NAMES.PHONE_DISABLED}
                color="secondary"
                onClick={
                  isAnonymousSurvey
                    ? undefined
                    : handleEmployeeNameClick(employeeResponse.employeeId)
                }
              />
            ) : employeeResponse.hasError ? (
              <MaterialDesignIcon iconName={ICON_NAMES.BLOCK} color="secondary" />
            ) : (
              <div />
            )}
          </Tooltip>
        )}
      </TableCell>
      {isAnonymousSurvey ? (
        <TableCell>
          <div className="typo-subtitle">Awaiting response</div>
        </TableCell>
      ) : (
        <>
          <TableCell>
            {employeeResponse.errorIsFixedByPhoneNumberUpdate ? (
              <div
                role="presentation"
                className="u-cursor-pointer"
                onClick={handleEmployeeNameClick(employeeResponse.employeeId)}
              >
                {employeeResponse.name}
              </div>
            ) : (
              <Link
                target="_blank"
                to={employeeMessagesRoute({ orgId, employeeId: employeeResponse.employeeId })}
              >
                {employeeResponse.name}
              </Link>
            )}
          </TableCell>
          <TableCell>
            <UiButton
              size="xs"
              variant="tertiary"
              onClick={handleEditClick(filteredResponses[0].surveyResponseId)}
            >
              Add Response
            </UiButton>
          </TableCell>
        </>
      )}
      <TableCell colSpan={2} />
    </TableRow>
  ) : (
    <>
      {filteredResponses.map((response, responseIndex) => (
        <TableRow
          key={
            responseIndex === 0
              ? employeeResponse.employeeId
              : `survey-answer-${response.surveyResponseId}`
          }
        >
          {responseIndex === 0 && (
            <>
              <TableCell style={{ width: '1em' }} rowSpan={filteredResponses.length}>
                <MaterialDesignIcon iconName={ICON_NAMES.CHECK} color="primary" />
                {employeeResponse.hasError && (
                  <Tooltip title={employeeResponse.errorMessage || ''}>
                    {employeeResponse.canRetry ? (
                      <MaterialDesignIcon
                        iconName={ICON_NAMES.REPLAY}
                        color="secondary"
                        onClick={handleRetryMessage(
                          employeeResponse.firstSurveyResponseId,
                          employeeResponse.retryMessageId,
                        )}
                      />
                    ) : employeeResponse.errorIsFixedByPhoneNumberUpdate ? (
                      <MaterialDesignIcon
                        iconName={ICON_NAMES.PHONE_DISABLED}
                        color="secondary"
                        onClick={handleEmployeeNameClick(employeeResponse.employeeId)}
                      />
                    ) : employeeResponse.hasError ? (
                      <MaterialDesignIcon iconName={ICON_NAMES.BLOCK} color="secondary" />
                    ) : (
                      <div />
                    )}
                  </Tooltip>
                )}
              </TableCell>
              {!isAnonymousSurvey && (
                <TableCell rowSpan={filteredResponses.length}>
                  {employeeResponse.errorIsFixedByPhoneNumberUpdate ? (
                    <div
                      role="presentation"
                      className="u-cursor-pointer"
                      onClick={handleEmployeeNameClick(employeeResponse.employeeId)}
                    >
                      {employeeResponse.name}
                    </div>
                  ) : (
                    <Link
                      target="_blank"
                      to={employeeMessagesRoute({ orgId, employeeId: employeeResponse.employeeId })}
                    >
                      {employeeResponse.name}
                    </Link>
                  )}
                </TableCell>
              )}
            </>
          )}
          <TableCell>
            <div>{cleanOptionalString(response.answer)}</div>
            {response.imageUrls &&
              response.imageUrls.map((url) => (
                <img
                  alt="sent in survey response"
                  role="presentation"
                  key={url}
                  src={url}
                  onClick={openImageInNewTab(url)}
                  className="u-cursor-pointer u-maxWidth-450px u-margin-0Auto"
                />
              ))}
          </TableCell>
          <TableCell>{response.answeredAt && formatDateTime(response.answeredAt)}</TableCell>
          <TableCell>
            <UiButton
              size="xs"
              variant="tertiary"
              onClick={handleToggleRelevance(
                response.surveyResponseId,
                response.employeeId,
                response.isRelevant,
              )}
            >
              {response.isRelevant ? 'Hide' : 'Unhide'}
            </UiButton>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
});
